import React from "react";
import Thermometer from "./Thermometer";
import {
  Box,
  CircularProgress,
  makeStyles,
  Typography
} from "@material-ui/core";
import UpdateIcon from "@material-ui/icons/Update";
import RoomIcon from "@material-ui/icons/Room";

import { DEVICE_TYPE } from "../../../domain/device";
import I18nText from "../../../elements/I18nText";
import { PrimaryButton } from "../../../elements/Button";
import { Link } from "react-router-dom";
import LastReportedDate from "./LastReportedDate";
import Status from "./Status";
import FormattedDate from "../../../elements/FormattedDate";
import { FORMAT_RELATIVE_DATE } from "../../../utils/formatting";

const useStyles = makeStyles(theme => ({
  container: {
    display: "flex",
    flexWrap: "wrap",
    width: "100%",
    height: 63,
    borderBottom: "1px solid #00000029"
  },
  topColumn: {
    height: 32,
    display: "flex",
    alignItems: "flex-end",
    paddingBottom: 2
  },
  bottomColumn: {
    height: 31,
    display: "flex",
    alignItems: "flex-start",
    paddingTop: 2
  },
  singleColumn: {
    height: 63,
    display: "flex",
    alignItems: "center"
  },
  metadata: {
    display: "flex",
    paddingLeft: 30,
    flex: "1 0"
  }
}));

const InfoBar = ({ device, allowedToQuery, isQuerying, onQueryDevice }) => {
  const classes = useStyles();

  const useBoldTextStyles = makeStyles({
    root: {
      fontSize: 13,
      fontWeight: "bold"
    }
  });

  const useTextStyles = makeStyles({
    root: {
      fontSize: 13
    }
  });

  const useLocationButtonStyles = makeStyles({
    root: {
      color: "black"
    }
  });

  const muiClasses = {
    text: useTextStyles(),
    boldText: useBoldTextStyles(),
    locationButton: useLocationButtonStyles()
  };

  const showQueryButton =
    (device.type === DEVICE_TYPE.SMS || device.type === DEVICE_TYPE.LX_IOT) &&
    device.ident &&
    allowedToQuery;

  return (
    <div className={classes.container}>
      <div className={classes.metadata}>
        <Box flex={1} minWidth={275}>
          <div className={classes.topColumn}>
            <Typography noWrap={true} classes={muiClasses.boldText}>
              #{device.id}
              {device.name && ` - ${device.name}`}
            </Typography>
          </div>
          <div className={classes.bottomColumn}>
            <Typography noWrap={true} classes={muiClasses.text}>
              {device.address.street} {device.address.streetNumber},{" "}
              {device.address.countryCode}-{device.address.postalCode}{" "}
              {device.address.city}
            </Typography>
          </div>
        </Box>
        <Box flex={2} paddingLeft={1}>
          <div className={classes.topColumn}>
            <Typography noWrap={true} classes={muiClasses.boldText}>
              {device.user.name}
            </Typography>
          </div>
          <div className={classes.bottomColumn}>
            <LastReportedDate
              date={device.lastReportedAt}
              status={device.status}
              variant="header"
            />
          </div>
        </Box>
      </div>
      {device.sensors && device.sensors.length > 0 && (
        <Box flex="0 1" paddingRight={4} display="flex" alignItems="center">
          <Thermometer
            sensorValue={device.sensors[0].temperatureValue}
            minThreshold={device.iotTempMin}
            maxThreshold={device.iotTempMax}
            showThermometerIcon={device.type === DEVICE_TYPE.IOT}
          />
        </Box>
      )}
      {device.location && (
        <Box flex="0 1" paddingRight={4}>
          <div className={classes.singleColumn}>
            <Link to={`/devices/map?highlight=${device.id}`} target="_blank">
              <RoomIcon classes={muiClasses.locationButton} fontSize="large" />
            </Link>
          </div>
        </Box>
      )}
      <Box flex="0 1" paddingRight={4}>
        <div className={classes.singleColumn}>
          <Status status={device.status} />
        </div>
      </Box>
      {showQueryButton && (
        <Box flex="0 1">
          <div
            className={classes.singleColumn}
            style={{
              selfAlign: "flex-end",
              justifyContent: "flex-end",
              paddingRight: 20
            }}
          >
            {isQuerying ? (
              <Box display="flex" justifyContent="center" alignItems="center">
                <CircularProgress color="primary" size={20} thickness={6} />
              </Box>
            ) : (
              <PrimaryButton icon={<UpdateIcon />} onClick={onQueryDevice}>
                <I18nText>devices.infoBar.query.action</I18nText>
              </PrimaryButton>
            )}
          </div>
        </Box>
      )}
    </div>
  );
};

export default InfoBar;
